import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FORM_ERROR, FormApi } from 'final-form';
import { isEqual } from 'lodash';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import {
    Navigate,
    unstable_useBlocker as useBlocker,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { showNotificationAsync } from '../../data-layer/notifications/actions';
import {
    removePostsAsync,
    resetPostCollection,
    setPostRecords,
} from '../../data-layer/posts/actions';
import * as postsService from '../../data-layer/posts/service';
import { ProductItemModel } from '../../data-layer/posts/types';
import { getProductsByUrl } from '../../data-layer/products/service';
import { useDispatch } from '../../data-layer/useDispatch';
import { toPostDataModel } from '../../mappers/posts/toPostDataModel';
import { toPostPersistenceModel } from '../../mappers/posts/toPostPersistenceModel';
import { toPostPersistenceUI } from '../../mappers/posts/toPostPersistenceUI';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { Alert, confirmAlert } from '../../ui/feedback/Alert';
import { Backdrop } from '../../ui/feedback/Backdrop';
import { IconClose } from '../../ui/icon/IconClose';
import { IconEye } from '../../ui/icon/IconEye';
import { IconText } from '../../ui/icon/IconText';
import { Button } from '../../ui/input/Button';
import { IconButton } from '../../ui/input/IconButton';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { Spin } from '../../ui/layout/Spin/Spin';
import { PostCard } from '../../ui/posts/PostCard/PostCard';
import { PostPersistenceForm } from '../../ui/posts/PostPersistenceForm/PostPersistenceForm';
import { PostPersistenceProducts } from '../../ui/posts/PostPersistenceProducts/PostPersistenceProducts';
import { Typography } from '../../ui/typography/Typography';
import { useCpaTrial } from '../../ui/users/CpaTrialDisclaimer/hooks/useCpaTrial';
import { canEditBusiness, isBloggerBusiness } from '../../utils/accessService';
import {
    trackEditPostFieldFilled,
    ymTrackEditPostDelete,
    ymTrackEditPostGetDraft,
    ymTrackEditPostPreview,
    ymTrackEditPostPublish,
    ymTrackEditPostSaveDraft,
    ymTrackProductNotFound,
    ymTrackRemoveProductFromPost,
} from '../../utils/metrika';
import { useExperimentFlag } from '../../utils/useExperimentFlag';
import { useMeta } from '../../utils/useMeta';
import { useTranslation } from '../../utils/useTranslation';
import { DialogRoute, useOpenDialog } from '../ModalRoute';
import { DIALOGS } from '../constants';
import { useCurrentUser } from '../users/useCurrentUser';
import { withBusinessAuthProtection } from '../withBusinessAuthProtection';
import { AddProductDialog } from './AddProductDialog';
import { DraftSelectionDialog } from './DraftSelectionDialog';
import { buildPreviewModel } from './buildPreviewModel';
import { usePostModel } from './usePostModel';
import type { MediaModel } from '../../data-layer/media/types';
import type { PostPersistenceUI } from '../../ui/posts/types';

export const PostPersistence = withBusinessAuthProtection(() => {
    const t = useTranslation();
    const [previewElement, setPreviewElement] = useState<ReactElement | null>(null);
    const [isDraftVisible, setIsDraftVisible] = useState<boolean>(false);
    const currentUser = useCurrentUser();
    const { isNotRegistered, refreshUserProfile } = useCpaTrial();
    const { postId = '' } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const formApiRef = useRef<null | FormApi<PostPersistenceUI>>(null);
    const postModel = usePostModel({ postId });
    const business = postModel?.business || currentUser?.profile;
    const businessId = business?.id;
    const businessPublicId = business?.publicId;
    const businessType = business?.type;
    const productRequired = isBloggerBusiness(businessType);
    const openDialog = useOpenDialog();
    const unsavedFormGuard = useBlocker(({ currentLocation, nextLocation }) => {
        const formState = formApiRef.current?.getState();

        if (nextLocation.pathname === currentLocation.pathname) {
            return false;
        }
        if (nextLocation?.state?.dialogName || currentLocation?.state?.dialogName) {
            return false;
        }

        return Boolean(formState?.dirty && !formState?.submitting);
    });
    const { state } = useLocation();
    const newProduct = state?.newProduct;

    const [products, setProducts] = useState<ProductItemModel[]>([]);

    useEffect(() => {
        setProducts(postModel?.products.items || []);
    }, [postModel?.products.items]);

    const showAddProductsDrawer = useCallback(() => {
        openDialog(DIALOGS.ADD_PRODUCT, {
            postId: postId,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openDialog, postModel?.id]);

    const [, onUrlPast] = useAsyncFn(
        async (url) => {
            try {
                const product = await getProductsByUrl(url);
                if (!products.find((item) => product.id === item.id)) {
                    setProducts([...products, product]);
                }
            } catch (err) {
                ymTrackProductNotFound({
                    page: 'Редактирование поста',
                    userLogin: currentUser?.login,
                    businessId: businessPublicId,
                    value: url,
                });
                throw new Error('Продукт не найден');
            }
        },
        [products, currentUser, businessPublicId],
    );

    const onUrlPastDebounced = useMemo(() => debounce(onUrlPast, 500), [onUrlPast]);

    const addNewProduct = useCallback(
        (product?: ProductItemModel) => {
            if (!products.find((item) => product?.id === item.id) && product) {
                setProducts([...products, product]);
            }
        },
        [products],
    );

    useEffect(() => {
        if (newProduct) {
            addNewProduct(newProduct);

            navigate(postId ? `/posts/${postId}/edit` : '/posts/create', {
                state: { newProduct: undefined },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newProduct]);

    const [productsChanged, setProductsChanged] = useState(false);

    const onNavigateToBusiness = useCallback(() => {
        if (currentUser?.profile?.publicId === businessPublicId) {
            navigate(`/businesses/${businessPublicId}/management`);

            return;
        }
        navigate(businessPublicId ? `/businesses/${businessPublicId}` : '/');
    }, [businessPublicId, currentUser, navigate]);

    const onUploadFile = useCallback(
        async (file: File): Promise<MediaModel | undefined> => {
            try {
                return await postsService.uploadPostMedia(file);
            } catch (error) {
                // @ts-ignore
                dispatch(showNotificationAsync(error?.message || 'Не удалось загрузить файл'));
            }

            return undefined;
        },
        [dispatch],
    );

    const onSubmit = useCallback(
        async (postUI: PostPersistenceUI) => {
            ymTrackEditPostPublish({
                page: 'Редактирование поста',
                userLogin: currentUser?.login,
                businessId: businessPublicId,
            });
            const persistenceModel = toPostPersistenceModel(
                {
                    ...postUI,
                    needCpaRegistration: isNotRegistered,
                },
                products,
            );

            const { data: updatedPost, error } = await postsService.persistPost(persistenceModel);

            if (error) {
                return {
                    [FORM_ERROR]: error,
                };
            }

            dispatch(setPostRecords(toPostDataModel(updatedPost)));

            if (businessPublicId) {
                dispatch(resetPostCollection('management'));
                dispatch(resetPostCollection(`business-overview-${businessPublicId}`));
                dispatch(resetPostCollection(`business-moderation-${businessPublicId}`));
            }

            if (isNotRegistered) {
                refreshUserProfile();
            }

            onNavigateToBusiness();
            unsavedFormGuard.reset?.();
        },
        [
            currentUser?.login,
            businessPublicId,
            isNotRegistered,
            products,
            dispatch,
            onNavigateToBusiness,
            unsavedFormGuard,
            refreshUserProfile,
        ],
    );

    const onRemove = useCallback(async () => {
        await confirmAlert(
            'Удаление',
            'Вы действительно хотите безвозвратно удалить пост?',
            'Отмена',
            'Удалить',
        );

        ymTrackEditPostDelete({
            page: 'Редактирование поста',
            userLogin: currentUser?.login,
            businessId: businessPublicId,
        });

        if (postId) {
            await dispatch(removePostsAsync(postId));
        }

        onNavigateToBusiness();
    }, [postId, currentUser?.login, businessPublicId, dispatch, onNavigateToBusiness]);

    const onSaveToDraft = useCallback(async () => {
        ymTrackEditPostSaveDraft({
            page: 'Редактирование поста',
            userLogin: currentUser?.login,
            businessId: businessPublicId,
        });
    }, [currentUser?.login, businessPublicId]);

    const onShowPreview = useCallback(() => {
        ymTrackEditPostPreview({
            page: 'Редактирование поста',
            userLogin: currentUser?.login,
            businessId: businessPublicId,
        });

        if (!formApiRef.current || !currentUser?.profile) {
            return;
        }

        const formState = formApiRef.current.getState();

        // @ts-ignore
        formApiRef.current?.getRegisteredFields().forEach(formApiRef.current.blur);

        if (formState.valid) {
            const draftModel = buildPreviewModel({
                persistenceUI: formState.values,
                businessUI: business,
                products,
            });

            setPreviewElement(
                <PostCard
                    index={0}
                    post={draftModel}
                    onShare={noop}
                    onExpand={noop}
                    onReaction={noop}
                    onBookmark={noop}
                    onSubscribe={noop}
                    onClickOutClick={noop}
                    onSlideChange={noop}
                    onVisibilityChange={noop}
                    onVideoMuteChange={noop}
                    onVideoEnd={noop}
                    onVideoStart={noop}
                    onVideoPlay={noop}
                    onVideoPause={noop}
                    renderMenu={(trigger) => trigger}
                    onProductButtonClick={noop}
                    onPostTextProductsClick={noop}
                    onPostTextCatalogClick={noop}
                    onPostTextClickoutClick={noop}
                    onStatusBadgeClick={noop}
                />,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, formApiRef.current, setPreviewElement, products]);

    const onHidePreview = useCallback(() => setPreviewElement(null), [setPreviewElement]);

    const onShowDraftDialog = useCallback(() => {
        ymTrackEditPostGetDraft({
            page: 'Редактирование поста',
            userLogin: currentUser?.login,
            businessId: businessPublicId,
        });
        setIsDraftVisible(true);
    }, [currentUser?.login, businessPublicId, setIsDraftVisible]);
    const onCloseDraftDialog = useCallback(() => setIsDraftVisible(false), [setIsDraftVisible]);

    useMeta({ title: `${postId ? 'Редактирование поста' : 'Добавление поста'} • Яндекс Ритм` });

    const onFieldBlur = useCallback(
        (fieldName: string, value: string) => {
            if (value) {
                trackEditPostFieldFilled(fieldName, {
                    page: 'Редактирование поста',
                    userLogin: currentUser?.login,
                    businessId: businessPublicId,
                    value,
                });
            }
        },
        [currentUser?.login, businessPublicId],
    );

    useEffect(() => {
        const initialProductsIds = postModel?.products.items
            ? postModel?.products.items.map((item) => item.id).sort()
            : [];
        const draftCategoriesIds = products.map((productDraft) => productDraft.id).sort();

        setProductsChanged(!isEqual(initialProductsIds, draftCategoriesIds));
    }, [postModel?.products.items, products, setProductsChanged]);

    const [, onRemoveProduct] = useAsyncFn(
        async (productId: string) => {
            try {
                await confirmAlert(
                    'Удаление товара',
                    'Вы уверены, что хотите удалить товар?',
                    'Отмена',
                    'Удалить',
                );
                setProducts(products.filter((item) => item.id !== productId));
                ymTrackRemoveProductFromPost({
                    page: 'Редактирование поста',
                    userLogin: currentUser?.login,
                    businessId: businessPublicId,
                });
            } catch (error) {
                return;
            }
        },
        [products, currentUser?.login, businessPublicId],
    );

    // Сбрасываем ошибку формы при изменениях в полях
    useEffect(() => {
        if (!formApiRef.current) {
            return;
        }

        const unsubscribe = formApiRef.current.subscribe(
            ({ values }) => {
                const formState = formApiRef.current?.getState();
                if (formState?.submitError) {
                    formApiRef.current?.restart(values);
                }
            },
            { values: true },
        );

        return unsubscribe;
    }, []);

    const isService = useExperimentFlag('enable_service_post') && business?.isService;

    if (postModel && !canEditBusiness(businessId, currentUser)) {
        return <Navigate to="/" replace />;
    }

    if (postId && !postModel) {
        return (
            <Box width="100vw" height="100vh" textAlign="center" style={{ alignContent: 'center' }}>
                <Spin />
            </Box>
        );
    }

    const initialValues = postModel ? toPostPersistenceUI(postModel) : { businessId };

    return (
        <DashboardPage maxWidth="1280px" centered={false} key={postModel?.id ?? 'create'}>
            <Flex
                width="100%"
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                px={{ xs: 1, sm: 2 }}
                mb={{ md: 1 }}
                py={1}
            >
                <Typography as="h1" variant="title2" weight="heavy">
                    {t(postModel ? 'Редактирование' : 'Добавить пост')}
                </Typography>
                <Flex direction="row" spacing={1}>
                    <Button spacing={1} onClick={onShowDraftDialog}>
                        <IconText size={16} />
                        <Box display={{ xs: 'none', sm: 'inline' }}>Черновики</Box>
                    </Button>
                    <Button variant="outlined" spacing={1} onClick={onShowPreview}>
                        <IconEye size={16} />
                        <Box display={{ xs: 'none', sm: 'inline' }}>Предпросмотр</Box>
                    </Button>
                </Flex>
            </Flex>
            <PostPersistenceForm
                ref={formApiRef}
                postId={postModel?.id}
                initialValues={initialValues}
                onUploadFile={onUploadFile}
                onSubmit={onSubmit}
                submitDisabled={Boolean(productRequired && !products.length)}
                onRemove={onRemove}
                onSaveToDraft={onSaveToDraft}
                onFieldBlur={onFieldBlur}
                user={currentUser}
                isProductsEnable={!isService}
                productsChanged={productsChanged}
                productsSlot={
                    products.length ? (
                        <PostPersistenceProducts
                            products={products}
                            onRemoveProduct={onRemoveProduct}
                            showAddProductsDrawer={showAddProductsDrawer}
                            businessType={businessType}
                        />
                    ) : undefined
                }
                onProductUrlPaste={onUrlPastDebounced}
                products={products}
                isSiteUrlEnabled={!isBloggerBusiness(businessType)}
            />

            {previewElement ? (
                <Backdrop onClose={onHidePreview}>
                    <Box position="relative" maxWidth="calc(100% - 112px)" width="360px">
                        <Box position="absolute" top={0} right="-48px">
                            <IconButton variant="filled" onClick={onHidePreview}>
                                <IconClose />
                            </IconButton>
                        </Box>
                        <Box width="100%" borderRadius={2} overflow="hidden">
                            {previewElement}
                        </Box>
                    </Box>
                </Backdrop>
            ) : null}

            {businessPublicId && isDraftVisible ? (
                <DraftSelectionDialog businessId={businessPublicId} onClose={onCloseDraftDialog} />
            ) : null}

            <DialogRoute name={DIALOGS.ADD_PRODUCT} element={<AddProductDialog />}></DialogRoute>

            {unsavedFormGuard.state === 'blocked' && (
                <Alert
                    title="Закрыть пост"
                    description="Все несохраненные изменения пропадут. Хотите перейти на другую страницу?"
                    actions={[
                        { label: 'Перейти', onClick: () => unsavedFormGuard.proceed?.() },
                        {
                            label: 'Остаться',
                            variant: 'filled',
                            onClick: () => unsavedFormGuard.reset?.(),
                        },
                    ]}
                />
            )}
        </DashboardPage>
    );
});
